import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Portal, Tooltip, IconButton, Radio, CircularProgress } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import NoDocumentsPdf from '../../../assests/pdf/NoDocument.pdf';
import styles from './DuplicateMergeModal.module.scss';

const hasPreviewUrl = (url) => Boolean(url) && url !== NoDocumentsPdf;

const DuplicateMergeModal = ({
  open,
  onClose,
  t,
  direction = 'rtl',
  rows = [],
  selectedRows = [],
  setSelectedRows,
  onMerge,
  documentType,
  caseId,
  beautifyNumber = (v) => v,
  formatDateNew = (v) => v,
}) => {
  const [primaryId, setPrimaryId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setPrimaryId(null);
      setExpandedId(null);
      setBusy(false);
      return;
    }
    setPrimaryId((current) => (current && selectedRows.includes(current) ? current : null));
    setExpandedId((current) => (current && selectedRows.includes(current) ? current : null));
  }, [open, selectedRows]);

  const selectedItems = useMemo(
    () =>
      selectedRows
        .map((id) => rows.find((row) => row.id === id))
        .filter(Boolean),
    [rows, selectedRows],
  );

  const expandedRow = selectedItems.find((row) => row.id === expandedId);
  const expandedUrl = expandedRow?.documentUrl;
  const canShowExpanded = hasPreviewUrl(expandedUrl);

  if (!open) return null;

  const canMerge = Boolean(primaryId) && selectedRows.length >= 2 && !busy;

  const handleClose = () => {
    if (busy) return;
    onClose();
  };

  const handleMerge = async () => {
    if (!canMerge) return;
    try {
      setBusy(true);
      await onMerge({
        caseId,
        documentType,
        primaryId,
        documentIds: selectedRows,
      });
      setSelectedRows([]);
      setPrimaryId(null);
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setBusy(false);
    }
  };

  const togglePreview = (event, id) => {
    event.stopPropagation();
    setExpandedId((current) => (current === id ? null : id));
  };

  const handleRemoveRow = (event, id) => {
    event.stopPropagation();
    if (busy) return;
    setSelectedRows((current) => (Array.isArray(current) ? current.filter((rowId) => rowId !== id) : []));
  };

  return (
    <Portal>
      <div
        className={`${styles.overlay} ${direction === 'rtl' ? styles.rtl : styles.ltr}`}
        onClick={handleClose}
      >
        <div dir={direction} onClick={(e) => e.stopPropagation()}>
          <div className={`${styles.modal} ${canShowExpanded ? styles.modalSplit : ''}`}>
            <div className={styles.header}>
              <Tooltip title={t('Close') || 'Close'}>
                <IconButton onClick={handleClose} className={styles.closeIcon} disabled={busy}>
                  <CancelIcon className={styles.closeIcon} />
                </IconButton>
              </Tooltip>
              <div>{t('MergeToolbarTitle')}</div>
              <div />
            </div>

            <div className={styles.body}>
              {canShowExpanded && (
                <div className={styles.previewPane}>
                  <div className={styles.previewPaneHeader}>
                    <span className={styles.previewPaneTitle}>
                      {expandedRow.reference || expandedRow.id}
                    </span>
                    <Tooltip title={t('Close') || 'Close'}>
                      <IconButton
                        size="small"
                        className={styles.expandBtn}
                        onClick={() => setExpandedId(null)}
                        aria-label={t('Close')}
                      >
                        <CloseFullscreenIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </div>
                  <iframe
                    className={styles.previewFrame}
                    src={`${expandedUrl}#page=1&view=FitH`}
                    title={expandedRow.reference || String(expandedRow.id)}
                  />
                </div>
              )}

              <div className={styles.listPane} dir={direction}>
                <div className={styles.hint}>{t('MergeToolbarHelp')}</div>

                {selectedItems.length === 0 ? (
                  <div className={styles.emptyCell}>{t('SelectAtLeastTwoItemsToMerge')}</div>
                ) : (
                  <div className={styles.list}>
                    {selectedItems.map((row) => {
                      const url = row.documentUrl || null;
                      const canPreview = hasPreviewUrl(url);
                      const isPrimary = primaryId === row.id;
                      const isExpanded = expandedId === row.id;

                      return (
                        <div
                          key={row.id}
                          className={`${styles.row} ${isPrimary ? styles.rowSelected : ''} ${isExpanded ? styles.rowExpanded : ''}`}
                        >
                          <div className={styles.rowMain}>
                            <button
                              type="button"
                              className={styles.pickArea}
                              onClick={() => setPrimaryId(row.id)}
                            >
                              <span className={styles.radioWrap}>
                                <Radio
                                  checked={isPrimary}
                                  size="small"
                                  sx={{ pointerEvents: 'none', color: 'var(--xtram-blue)' }}
                                />
                              </span>
                              <span className={styles.meta}>
                                <span className={styles.referenceRow}>
                                  <span className={styles.field}>
                                    <span className={styles.label}>{t('Reference')}</span>
                                    <span className={`${styles.value} ${styles.reference}`}>{row.reference || row.id}</span>
                                  </span>
                                  {isPrimary && (
                                    <span className={styles.primaryBadge}>{t('ChoosePrimary')}</span>
                                  )}
                                </span>
                                <span className={styles.descriptionField} title={row.description || ''}>
                                  <span className={styles.label}>{t('Description')}</span>
                                  <span className={`${styles.value} ${styles.description}`}>{row.description || ''}</span>
                                </span>
                                <span className={styles.details}>
                                  <span className={styles.field}>
                                    <span className={styles.label}>{t('Amount')}</span>
                                    <span className={styles.value}>{beautifyNumber(row.amount)}</span>
                                  </span>
                                  <span className={styles.dot}>•</span>
                                  <span className={styles.field}>
                                    <span className={styles.label}>{t('DocumentDate')}</span>
                                    <span className={styles.value}>{formatDateNew(row.documentDate)}</span>
                                  </span>
                                </span>
                              </span>
                            </button>

                            <div className={styles.thumb}>
                              {canPreview ? (
                                <iframe
                                  src={`${url}#page=1&view=FitH`}
                                  title={row.reference || String(row.id)}
                                  className={styles.thumbFrame}
                                />
                              ) : (
                                <div className={styles.thumbEmpty}>{t('NoDocumentsFound')}</div>
                              )}
                              <Tooltip title={t('Preview')}>
                                <span className={styles.expandWrap}>
                                  <IconButton
                                    size="small"
                                    className={styles.expandBtn}
                                    onClick={(event) => togglePreview(event, row.id)}
                                    aria-label={t('Preview')}
                                    disabled={!canPreview}
                                  >
                                    {isExpanded ? (
                                      <CloseFullscreenIcon fontSize="small" />
                                    ) : (
                                      <OpenInFullIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </div>
                            <button
                              type="button"
                              className={styles.removeBtn}
                              onClick={(event) => handleRemoveRow(event, row.id)}
                              disabled={busy}
                            >
                              {t('RemoveFromQueue')}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className={styles.footer}>
              <button type="button" className={styles.cancel} onClick={handleClose} disabled={busy}>
                {t('Close')}
              </button>
              <button
                type="button"
                className={styles.save}
                onClick={handleMerge}
                disabled={!canMerge}
              >
                {busy ? (
                  <CircularProgress size={22} sx={{ color: 'var(--xtram-white)' }} />
                ) : (
                  t('MergeSelected')
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
};

DuplicateMergeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  direction: PropTypes.string,
  rows: PropTypes.array,
  selectedRows: PropTypes.array,
  setSelectedRows: PropTypes.func.isRequired,
  onMerge: PropTypes.func.isRequired,
  documentType: PropTypes.string,
  caseId: PropTypes.string,
  beautifyNumber: PropTypes.func,
  formatDateNew: PropTypes.func,
};

export default DuplicateMergeModal;
